/* global Sys, Core, Services, Language, Resources */
Sys.ns("Core.Slots");

/**
 * The Free Rounds controller.
 *
 * @class Core.Slots.FreeRoundsController
 * @extends Core.Controller
 */
Core.Slots.FreeRoundsController = {

    constructor: function() {
        Core.Slots.FreeRoundsController.superclass.constructor.apply(this, arguments);
    },

    /**
     * @protected
     * @inheritdoc
     */
    init: function() {
        Core.Slots.FreeRoundsController.superclass.init.apply(this, arguments);
    },

    /**
     * @protected
     * @inheritdoc
     */
    setupEvents: function() {
        this.on({
            "notify:stateHandler.enteringFreeRoundsDialogState": this.showEnforcedContinueDialog,
            "notify:stateHandler.enteringCheckForAdditionalFreeRoundsState": this.requestGameConfiguration,
            "notify:responseParser.gameConfigurationParsed": this.onGameConfigurationParsed,
            "request:freeRounds.requestGameConfiguration": this.requestGameConfiguration
        });
    },

    /**
     * Sets up the game for free rounds and shows a dialog.
     *
     * @private
     * @returns {void}
     */
    startFreeRounds: function() {
        this.freeRoundsStarted = true;
        this.addListener("notify:stateHandler.enteringIdleState", this.onEnteringIdleState);
        this.addListener("notify:stateHandler.leavingIdleState", this.onLeavingIdleState);

        this.fireEvent("request:maxBetButton.disable", this.MODULE_NAME);
        this.fireEvent("request:betSettings.disable", this.MODULE_NAME);

        this.fireEvent("notify:freeRounds.started", Services.freeRounds.getRemaining());
        this.setFreeroundModeParameter(true);

        this.model.storeData("hasShownSplash", true);
        this.model.storeData("totalFreeRounds", Services.freeRounds.getRemaining());
    },

    /**
     * Ends free rounds and resets the game to normal mode.
     *
     * @private
     * @returns {void}
     */
    endFreeRounds: function() {
        this.freeRoundsStarted = false;

        this.showFreeRoundsEndDialog();

        // Stop listening to game round event since we're done with free rounds
        this.removeListener("notify:stateHandler.enteringIdleState");
        this.removeListener("notify:stateHandler.leavingIdleState");

        this.fireEvent("request:maxBetButton.enable", this.MODULE_NAME);

        // Notify other modules that free rounds has officially ended
        this.fireEvent("notify:freeRounds.ended");
    },

    /**
     * Sets the free round mode parameter.
     *
     * @private
     * @param {boolean}[enabled=false] If to enable or disable, defaults to false.
     * @returns {void}
     */
    setFreeroundModeParameter: function(enabled) {
        this.setExtraParameter("freeroundmode", enabled);
    },

    /**
     * Sets/update the extra param for server request.
     *
     * @private
     * @param {string} param Parameter to update.
     * @param {*} value Value to set.
     * @returns {void}
     */
    setExtraParameter: function(param, value) {
        var params = Resources.readData("extraParams");

        if (Sys.isObj(params)) {
            params[param] = value;
            Resources.storeData("extraParams", params);
        }
    },

    /**
     * If no free rounds are left, requests the end free round dialog.
     *
     * @private
     * @returns {void}
     */
    onEnteringIdleState: function() {
        this.requestCounterUpdate();
    },

    /**
     * Fires the `notify:freeRounds.nextRound` event on leaving idle state.
     *
     * @private
     * @returns {void}
     */
    onLeavingIdleState: function() {
        this.notifyFreeRoundsLeft();
    },

    /**
     * Update the free rounds left counter.
     *
     * @returns {void}
     */
    notifyFreeRoundsLeft: function() {
        var totalRounds = this.model.readData("totalFreeRounds"),
            freeRoundsLeft = Services.freeRounds.getRemaining();

        this.fireEvent("notify:freeRounds.nextRound", totalRounds, freeRoundsLeft);
    },

    /**
     * Request new game information from the server.
     *
     * @private
     * @returns {void}
     */
    requestGameConfiguration: function() {
        // Stop autoplay since we're out of rounds
        this.fireEvent("request:autoPlayer.stop");
        // Set free rounds mode to false since we don't know yet if we will get more.
        this.setFreeroundModeParameter(false);
        this.fireEvent("request:serverManager.sendAction", "init", "notify:responseParser.gameConfigurationParsed", "notify:serverManager.reInitRequestFailed");
    },

    /**
     * Handler for storing new data on new init-response.
     *
     * @private
     * @param {Object} parsedResponse The parsed response, unused here but included to be able to use responseParser event.
     * @returns {void}
     */
    onGameConfigurationParsed: function(parsedResponse) {
        var me = this,
            nextActionIsValid = (parsedResponse.nextaction === "spin");

        // Used in close dialog when a widget is detected in reInit after other free round programs.
        this.bonusProgramWidgetQueued = Boolean(parsedResponse.freeRoundWidgetEnabled);
        this.bonusProgramWidgetActive = Boolean(Resources.readData("bonusProgramWidgetActive"));

        this.model.storeData("hasShownSplash", false);

        if (Sys.isNumber(parsedResponse.freeRoundsLeft) && parsedResponse.freeRoundsLeft > 0) {
            Services.freeRounds.setFreeRoundsLeft(parsedResponse.freeRoundsLeft);

            if (parsedResponse.isLowWinFreeRound) {
                this.showExtraFreeRoundsDialog();
            }
            else if (this.bonusProgramWidgetActive) {
                // Tell cash field, money manager, bet settings to update their values/states.
                this.fireEvent("notify:freeRounds.reInitResponseReceived", parsedResponse);
                this.startFreeRounds();
                Resources.removeData("bonusProgramWidgetActive");
            }
            else {
                this.showFreeRoundsEndDialog(function() {
                    me.showEnforcedContinueDialog(function() {
                        me.fireEvent("notify:freeRounds.reInitResponseReceived", parsedResponse);
                        me.fireEvent("notify:dialogBoxClosed", { name: "freeRoundsStart", button: "continue" });
                        me.startFreeRounds();
                    });
                });
                me.fireEvent("request:cashField.showBasicCashValues");
            }

            this.requestCounterUpdate();
            this.notifyFreeRoundsLeft();
            this.setFreeroundModeParameter(true);
        }
        else if (!nextActionIsValid) {
            this.showFreeRoundsReloadDialog();
        }
        else if (this.freeRoundsStarted) {
            this.endFreeRounds();
        }
    },

    /**
     * Requests an update to the free rounds counter.
     *
     * @private
     * @returns {void}
     */
    requestCounterUpdate: function() {
        Services.storage.storeData("freeRounds.roundsLeft", Services.freeRounds.getRemaining());
    },

    /**
     * Fires event to close free rounds dialog.
     * Calls bonus program widget if widget program is available.
     *
     * @private
     * @returns {void}
     */
    closeFreeRoundsDialog: function() {
        this.fireEvent("notify:freeRounds.dialogClosed");

        if (this.bonusProgramWidgetQueued) {
            this.fireEvent("notify:bonusProgramWidget.reInit", Resources.readData("gameServerInitResponse"));
            this.bonusProgramWidgetQueued = false;
        }
    },

    /**
     * Show a dialog stating user has won new free rounds.
     *
     * @private
     * @returns {void}
     */
    showExtraFreeRoundsDialog: function() {
        // Use default String for extra free rounds (Not present in language.xml)
        var me = this;

        me.requestDialog({
            title: Services.languageManager.getText(Language.Keys.freeRoundsExtraTitle),
            text: Services.languageManager.getText(Language.Keys.freeRoundsExtraWon, [Services.freeRounds.getRemaining()]),
            buttons: [{
                scope: this,
                text: Services.languageManager.getText(Language.Keys["continue"]),
                action: function() {
                    me.fireEvent("notify:dialogBoxClosed", { name: "freeRoundsExtra", button: "continue" });
                    me.closeFreeRoundsDialog();
                }
            }]
        });
    },

    /**
     * Show a dialog stating that free rounds has ended and forces  a reload of the game.
     *
     * @private
     * @returns {void}
     */
    showFreeRoundsReloadDialog: function() {
        var me = this;

        me.requestDialog({
            title: Services.languageManager.getText(Language.Keys.freeRoundsFinished),
            text: Services.languageManager.getText(Language.Keys.roundsUseAcctMoney) + " " + Services.languageManager.getText(Language.Keys.reload),
            buttons: [{
                scope: this,
                text: Services.languageManager.getText(Language.Keys.btn_reload),
                action: function() {
                    me.fireEvent("notify:dialogBoxClosed", { name: "freeRoundsReload", button: "reload" });
                    Sys.utils.reload();
                }
            }]
        });
    },

    /**
     * Show a dialog when free rounds ends.
     *
     * @private
     * @param {Function} [actionCallback] Callback function when button action is fired.
     * @returns {void}
     */
    showFreeRoundsEndDialog: function(actionCallback) {
        var me = this;

        me.requestDialog({
            title: Services.languageManager.getText(Language.Keys.freeRoundsFinished),
            text: Services.languageManager.getText(Language.Keys.roundsUseAcctMoney),
            buttons: [{
                scope: this,
                text: Services.languageManager.getText(Language.Keys["continue"]),
                action: function() {
                    if (actionCallback) {
                        actionCallback();
                    }
                    me.fireEvent("notify:dialogBoxClosed", { name: "freeRoundsEnd", button: "continue" });
                    me.closeFreeRoundsDialog();
                }
            }]
        });
    },

    /**
     * Show a dialog with a continue button.
     *
     * @private
     * @param {Function} [action] The action for the button.
     * @returns {void}
     */
    showEnforcedContinueDialog: function(action) {
        var me = this;

        me.requestDialog({
            title: Services.languageManager.getText(Language.Keys.roundsLeft),
            text: Services.languageManager.getText(Language.Keys.haveFreeRounds, [Services.freeRounds.getRemaining()]),
            buttons: [{
                scope: this,
                text: Services.languageManager.getText(Language.Keys["continue"]),
                action: action || function() {
                    me.fireEvent("notify:dialogBoxClosed", { name: "freeRoundsStart", button: "continue" });
                    me.startFreeRounds();
                }
            }]
        });
    },

    /**
     * Request a dialog from Dialog Manager.
     *
     * @param {IDialogConfig} config Dialog config.
     * @returns {void}
     */
    requestDialog: function(config) {
        Services.dialog.show(config);
    }
};

Core.Slots.FreeRoundsController = Sys.extend(Core.Controller, Core.Slots.FreeRoundsController, "Core.Slots.FreeRoundsController");
